import React, { Suspense, useMemo, useRef, useState } from 'react'
import './App.scss'
import SplashSpinner from './components/SplashSpinner/SplashSpinner'
import useOnMount from './hooks/useOnMount'
import {
  LOGIN_CALLBACK_URL,
  PREFERENCES_SAVED_URL,
  BLOG_URL,
  ART_COLLECTION_HOME_URL,
} from './config/config'
import './registerSW'
import { photosTheme as theme } from 'mixtiles-web-common'
import { Route, useLocation } from '@remix-run/react'
import { analytics, EVENT_NAMES } from './services/Analytics/Analytics'
import { initEntrypoint } from './Entrypoint.utils'
import BrowserThemeColor from './components/ThemeColor/BrowserThemeColor'
import { setLastAction } from './hooks/useCrashLogger'
import { lazyImport } from './import'
import { logger } from './services/logger'
import { useIdleTimer } from 'react-idle-timer'
import { useUser } from 'services/UserProvider'
import PreferencesSaved from './pages/PreferencesSaved/PreferencesSaved'
import { useExperimentManager } from './services/ExperimentManager/ExperimentManager'
import { SentryRoutes } from './SentryRoutes'
import { useTilesSource } from 'components/TilesProvider/TilesSourceContext'
import ArtCollectionHome from './pages/ArtCollection/ArtCollectionHome/ArtCollectionHome'
import { ArtCollectionProvider } from './pages/ArtCollection/ArtCollectionProvider/ArtCollectionProvider'
import { ONE_DAY_IN_MILLISECONDS } from './App.consts'
import { materialsAsColorManager } from './services/MaterialsAsColorManager/MaterialsAsColorManager'

const LazyApp = lazyImport(() => import('./App'), 'App')
const LazyJobPositionPageWrapper = lazyImport(
  () => import('./pages/JobPositionPage/JobPositionPageWrapper'),
  'JobPositionPageWrapper'
)
const LazyBlogEntryPoint = lazyImport(
  () => import('./pages/Blog/BlogEntryPoint'),
  'BlogEntryPoint'
)

function AppLoader(props: { skipTilesProvider: boolean }) {
  return (
    <Suspense fallback={<SplashSpinner />}>
      <LazyApp {...props} />
    </Suspense>
  )
}

function EntryPointRoutes() {
  const location = useLocation()
  const experimentManager = useExperimentManager()

  const skipTilesProvider = useMemo(
    () => location.pathname === LOGIN_CALLBACK_URL,
    [location.pathname]
  )

  return (
    <SentryRoutes>
      <Route path={PREFERENCES_SAVED_URL} element={<PreferencesSaved />} />
      {experimentManager.isEnabled('blog') && (
        <Route path={`${BLOG_URL}/*`} element={<LazyBlogEntryPoint />} />
      )}
      <Route
        path={ART_COLLECTION_HOME_URL}
        element={
          <ArtCollectionProvider>
            <ArtCollectionHome />
          </ArtCollectionProvider>
        }
      />
      <Route
        path="/positions/:jobId"
        element={<LazyJobPositionPageWrapper />}
      />
      <Route
        path="*"
        element={<AppLoader skipTilesProvider={skipTilesProvider} />}
      />
    </SentryRoutes>
  )
}

function EntryPointSelection({
  loadingStartTime,
}: {
  loadingStartTime: number
}) {
  const location = useLocation()

  useOnMount(() => {
    setLastAction(EVENT_NAMES.ENTRYPOINT_RENDERED)
    analytics.track(EVENT_NAMES.ENTRYPOINT_RENDERED, {
      'Full URL': window.location.href,
      Path: location.pathname,
      Time: Date.now() - loadingStartTime,
    })
  })

  const headComponent = (
    <BrowserThemeColor color={theme.colors.grayBackgroundWhite} />
  )

  // We are separating the homepage, onboarding and positions pages into separate routes
  // so they can load faster and all the rest will trigger the loading of the App
  return (
    <Suspense fallback={<SplashSpinner />}>
      {headComponent}
      <EntryPointRoutes />
    </Suspense>
  )
}

let globalEntrypointInitialized = false

export default function EntryPoint() {
  const { tilesSource } = useTilesSource()
  const [entrypointInitialized, setEntrypointInitialized] = useState(
    globalEntrypointInitialized
  )
  const user = useUser()

  const { getElapsedTime } = useIdleTimer({
    onActive: () => {
      analytics.track(EVENT_NAMES.APP_REFRESHED_AFTER_LONG_IDLE, {
        'Idle time in MS': getElapsedTime(),
      })
      setTimeout(() => {
        // added timeout to give analytics time to send the event
        location.reload()
      }, 2000)
    },
    timeout: ONE_DAY_IN_MILLISECONDS / 2,
  })

  const beforeInitialization = useRef(Date.now())

  useOnMount(() => {
    if (entrypointInitialized) {
      return
    }
    initEntrypoint(user, tilesSource)
      .then(() => {
        setEntrypointInitialized(true)
        materialsAsColorManager.init(
          !!window.__remixContext?.state?.loaderData?.root
            .isMaterialsUnderFrameEnabled
        )
        globalEntrypointInitialized = true
      })
      .catch((e) => {
        logger.error('failed to initialize entrypoint', e)
      })
  })

  if (!entrypointInitialized) {
    return <SplashSpinner />
  }

  return <EntryPointSelection loadingStartTime={beforeInitialization.current} />
}
